import React, { useEffect, useState } from "react";
import {
  Button,
  Checkbox,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Progress,
} from "@chakra-ui/react";
import { Plus } from "lucide-react";
import Pagination from "../../../utils/pagination/Pagination";
import CustomerLedgerForm from "./CustomerLedgerForm";
import { getCustomerLedger } from "../../../redux/reduxApi/customerLedgerReduxApi";
import { pageData } from "../../../utils/paginationConfig";
import { useDispatch, useSelector } from "react-redux";
import { deleteCustomerLedger, countCustomerLedger } from "../../../redux/api/customerLedger/CustomerLedgerApi";
import { deleteCustomerLedgerAction } from "../../../redux/reduxSlice/customerLedgerSlice";
import { toast } from "react-toastify";
import EllipsisMenu from "../../../utils/EllipsisDropdown/EllipsisMenu";
import { useNavigate } from "react-router-dom";

const CustomerLedgerList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(false);
  const [showProgressBar, setShowProgressBar] = useState(false);
  const [deleteCustomerLedgerROWID, setDeleteCustomerLedgerROWID] = useState("");
  const [data, setData] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [dataToEdit, setDataToEdit] = useState(null);
  const [isUpdateMode, setIsUpdateMode] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [totalPages, setTotalPages] = useState(5);
  const [customerLedgerData, setCustomerLedgerData] = useState([]);
  const permissions = localStorage.getItem("permissions");
  const filterData = JSON.parse(permissions);

    
  const requiredPermissions = ["add", "viewAll", "fullAccess", "modifyAll", "delete", "assignOwner"];
  
  const allModulesHaveFullPermissions = Array.isArray(filterData) && filterData.length > 0 && filterData.every((module) => requiredPermissions.every((permission) => module[permission] === true));

  
  const getCustomerLedgerData = useSelector((state) => state?.customerLedger?.customerLedger);

  // Get customer ledger data
  useEffect(() => {
    const page = 1;
    setLoading(true);
    dispatch(getCustomerLedger({ page: currentPage, limit: 30, size: 30 }))
      .then((response) => {})
      .catch((error) => {
        console.log(error);
      })
      .finally(() => {
        setLoading(false);
        setTimeout(() => {}, [300]);
      });
  }, [dispatch, currentPage]);

  useEffect(() => {
    if (currentPage % pageData.apiHit === 0) {
      const page = currentPage / pageData.apiHit + 1;
      dispatch(getCustomerLedger(page));
    }
  }, [totalPages]);

  // Set customer ledger data from Redux store
  useEffect(() => {
    setCustomerLedgerData(getCustomerLedgerData || []);
  }, [getCustomerLedgerData]);

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return dateString.split(' ')[0]; // Return only the date part
  };

  // total companies count
  const fetchTotalCount = async () => {
    try {
      const response = await countCustomerLedger();
      const count = parseInt(response.data.count, 10);
      setTotalCount(count);
    } catch (error) {
      console.error("Error fetching total Attendance count:", error);
    }
  };

  useEffect(() => {
    fetchTotalCount();
  }, []);

  // Handle Delete Function - FIXED
  const handleDelete = () => {
    setShowProgressBar(true);
    setLoading(true);
    
    // Use the correct ROWID - either from selected rows or the single delete ROWID
    const idsToDelete = selectedRows.length > 0 ? selectedRows : [deleteCustomerLedgerROWID];
    
    console.log("Deleting rows with IDs:", idsToDelete); // Debug log
    
    Promise.all(idsToDelete.map((id) => deleteCustomerLedger(id)))
      .then(() => {
        // Update local state
        setCustomerLedgerData(prevData => 
          prevData.filter(item => !idsToDelete.includes(item.ROWID))
        );
        
        // Update Redux store
        dispatch(deleteCustomerLedgerAction(idsToDelete));
        
        // Refresh counts and data
        fetchTotalCount();
        
        // Refresh the data by calling the API again
        dispatch(getCustomerLedger({ page: currentPage, limit: 30, size: 30 }));
        
        toast.success("Row(s) deleted successfully");
      })
      .catch((error) => {
        console.log("Delete error:", error);
        toast.error("Failed to delete row(s)");
      })
      .finally(() => {
        setShowProgressBar(false);
        setLoading(false);
        closeTrash();
        setSelectedRows([]);
        setDeleteCustomerLedgerROWID("");
      });
  };

  // handle update
  const handleUpdate = (item) => {
    setDataToEdit(item);
    setIsUpdateMode(true);
    openDrawer();
  };

  // Single Select
  const handleRowSelect = (ROWID) => {
    const isSelected = selectedRows.includes(ROWID);
    let newSelectedRows = [];

    if (!isSelected) {
      newSelectedRows = [...selectedRows, ROWID];
    } else {
      newSelectedRows = selectedRows.filter((rowId) => rowId !== ROWID);
    }
    setSelectedRows(newSelectedRows);
  };

  // All Select
  const handleSelectAllRows = () => {
    const allSelected = selectedRows.length === customerLedgerData.length;
    let newSelectedRows = [];
    if (!allSelected) {
      newSelectedRows = customerLedgerData.map((item) => item.ROWID);
    }
    setSelectedRows(newSelectedRows);
  };

  const openDrawer = () => {
    setIsDrawerOpen(true);
  };

  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setIsUpdateMode(false);
    setDataToEdit(null);
  };

  const openTrash = (id) => {
    setDeleteCustomerLedgerROWID(id);
    setIsOpen(true);
  };

  const closeTrash = () => {
    setIsOpen(false);
    setDeleteCustomerLedgerROWID("");
  };

  return (
    <>
      {/* Spinner and Progress */}
      {loading && (
        <div className="spinner-overlay">
          <Spinner size="xl" color="pink.500" />
        </div>
      )}
      {showProgressBar && (
        <Progress size="xs" isIndeterminate colorScheme="pink" />
      )}

      {/* 🗑️ Trash Confirmation Modal */}
      <Modal isOpen={isOpen} onClose={closeTrash}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Are you sure you want to Delete?</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {selectedRows.length > 0 
              ? `You are about to delete ${selectedRows.length} record(s). This action cannot be undone.`
              : "You are about to delete this record. This action cannot be undone."}
          </ModalBody>
          <ModalFooter>
            <Button colorScheme="red" mr={3} onClick={handleDelete}>
              Delete
            </Button>
            <Button variant="outline" onClick={closeTrash}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Header + Button */}
      <div className="d-flex flex-column align-items-end">

         {selectedRows.length > 0 && (
                      <>
                      
        
                                    {allModulesHaveFullPermissions && (
        
                          <button className="new-button mx-2" onClick={openTrash}>
                            <Plus size="18px" />
                            <span className="ms-1">Delete All</span>
                          </button>
                        )}
                      </>
                    )}

                    {selectedRows.length === 0 && (
                                    <>
                                      
                                      {filterData?.find((mod) => mod.moduleName === "Customer Ledger" && mod.add === true) && (
                                         <button
          className="new-button mx-2 btn btn-primary"
          onClick={openDrawer}
        >
          <Plus size="18px" />
          <span className="ms-1">New</span>
        </button>
                                      )}
                                    </>
                                  )}

       

        {/* Table Section */}
        <div
          className="custom-scroll w-100 mt-3"
          style={{ height: "70vh", overflowX: "auto" }}
        >
          <TableContainer
            className='card card-detail mt-1 scrollable-container'
            style={{
              maxHeight: "70vh",
              position: "relative",
              overflowY: "scroll",
              scrollbarColor: "#A0AEC0 #EDF2F7",
              scrollbarWidth: "10px",
            }}
          >
            <Table variant="simple" size="sm">
              <Thead>
                <Tr>
                  <Th className="py-1 th-color">
                    <Checkbox
                      isChecked={selectedRows.length === customerLedgerData.length && customerLedgerData.length > 0}
                      onChange={handleSelectAllRows}
                    />
                  </Th>
                  <Th className="py-1 th-color">Actions</Th>
                  <Th className="py-1 th-color">Site ID</Th>
                  <Th className="py-1 th-color">Customer Name</Th>
                  <Th className="py-1 th-color">Site in KW</Th>
                  <Th className="py-1 th-color">Project Amount</Th>
                  <Th className="py-1 th-color">Payment Pending</Th>
                  <Th className="py-1 th-color">Payment Received</Th>
                  <Th className="py-1 th-color">Payment Type</Th>
                  <Th className="py-1 th-color">Due Date</Th>
                  <Th className="py-1 th-color">Due Payment</Th>
                  <Th className="py-1 th-color">Work Status</Th>
                  <Th className="py-1 th-color">Remark</Th>
                  <Th className="py-1 th-color">Finalized By</Th>
                  <Th className="py-1 th-color">Last Follow Up</Th>
                  <Th className="py-1 th-color">Total Reminder</Th>
                  <Th className="py-1 th-color">Total Visit for Payment</Th>
                </Tr>
              </Thead>

              <Tbody>
                {customerLedgerData.map((item) => (
                  <Tr key={item.ROWID} className="tr-border" style={{ cursor: "pointer", position: "relative" }}>
                    <Td>
                      <Checkbox
                        isChecked={selectedRows.includes(item.ROWID)}
                        onChange={() => handleRowSelect(item.ROWID)}
                      />
                    </Td>
                    <Td style={{ width: "110px", textAlign: "center" }} onClick={(e) => e.stopPropagation()}>
                      <EllipsisMenu
                        item={item}
                        onView={() => navigate("/customer-ledger-detail-view", { state: { ROWID: item?.ROWID } })}
                        onEdit={() => handleUpdate(item)}
                        onDelete={() => openTrash(item.ROWID)}
                        permissions={{
                          canEdit: filterData?.find((mod) => mod.moduleName === "Customer Ledger" && mod.modifyAll === true),
                          canDelete: filterData?.find((mod) => mod.moduleName === "Customer Ledger" && mod.delete === true),
                          canView: true,
                        }}
                      />
                    </Td>
                    <Td className="font-colors">{item.siteId || "-"}</Td>
                    <Td className="font-colors">{item.customerName}</Td>
                    <Td className="font-colors">{item.siteInKw} KW</Td>
                    <Td className="font-colors">₹{item.projectAmount}</Td>
                    <Td className="font-colors">₹{item.paymentPending}</Td>
                    <Td className="font-colors">₹{item.paymentReceived}</Td>
                    <Td className="font-colors">{item.paymentType}</Td>
                    <Td className="font-colors">{formatDate(item.dueDate)}</Td>
                    <Td className="font-colors">{formatDate(item.duePayment)}</Td>
                    <Td className="font-colors">{item.workStatus}</Td>
                    <Td className="font-colors">{item.remark}</Td>
                    <Td className="font-colors">{item.finalizeBy}</Td>
                    <Td className="font-colors">{item.lastFollowUp}</Td>
                    <Td className="font-colors">{item.totalReminder}</Td>
                    <Td className="font-colors">{item.totalVisitForPayment}</Td>
                  </Tr>
                ))}
                {customerLedgerData.length === 0 && !loading && (
                  <Tr>
                    <Td colSpan={18} className="text-center">
                      No data available
                    </Td>
                  </Tr>
                )}
              </Tbody>
            </Table>
          </TableContainer>
        </div>

        {/* Footer */}
        <div className="footer">
          <div className="footer d-flex fixed-bottom start-20">
            <div
              className="total"
              style={{ marginLeft: "16%", marginTop: "15px" }}
            >
              <p className="fw-semibold">Total Count: {totalCount}</p>
            </div>
            {/* Pagination */}
            <div className="d-flex align-items-center justify-content-center position-fixed-bottom mx-auto">
              <Pagination
                totalPages={Math.ceil(totalCount / 30)}
                onPageChange={setCurrentPage}
              />
            </div>
            {/* End Pagination */}
            <CustomerLedgerForm
              closeDrawer={closeDrawer}
              isUpdateMode={isUpdateMode}
              isDrawerOpen={isDrawerOpen}
              setIsUpdateMode={setIsUpdateMode}
              dataToEdit={dataToEdit}
              refreshData={() => {
                dispatch(getCustomerLedger({ page: currentPage, limit: 30, size: 30 }));
                fetchTotalCount();
              }}
            />
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomerLedgerList;